import React, { useState, useEffect, useCallback } from "react"
import PropTypes from "prop-types"

import JobApplication from "../components/JobApplication"
import ApplicantActions from "../components/ApplicantActions"
import ApplicantThumb from "../components/ApplicantThumb"
import Player from "../components/Player"
import Client from "../api/Client"

const propTypes = {
    jobApplications: PropTypes.array.isRequired,
    allowToggle: PropTypes.bool.isRequired,
    onModalOpen: PropTypes.func,
    loading: PropTypes.bool,
    empty: PropTypes.node,
    layout: PropTypes.string,
};

const LAYOUTS = ['table', 'compact', 'grid'];

const SkeletonRows = () => (
    Array.from({ length: 3 }).map((_, index) => (
        <tr className="job-application-row job-application-row--skeleton" key={`skeleton-${index}`}>
            <td className="job-application-row__video">
                <div className="applicants-skel applicants-skel--video" />
            </td>
            <td className="job-application-row__info">
                <div className="applicants-skel" />
                <div className="applicants-skel" />
                <div className="applicants-skel applicants-skel--short" />
            </td>
            <td className="job-application-row__fact">
                <div className="applicants-skel" />
                <div className="applicants-skel applicants-skel--short" />
            </td>
            <td className="job-application-row__actions">
                <div className="applicants-skel applicants-skel--actions" />
            </td>
        </tr>
    ))
);

const ApplicantSheet = ({ jobApplication, allowToggle, onModalOpen, onHide, onPlay, onClose }) => {
    const applicant = jobApplication.attributes.applicant;

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKey);
        };
    }, [onClose]);

    const handleHide = (id) => {
        onHide(id);
        onClose();
    };

    return (
        <div className="applicant-sheet" role="dialog" aria-modal="true" aria-label={applicant.name}>
            <button type="button" className="applicant-sheet__backdrop" onClick={onClose} aria-label="Close details" />
            <div className="applicant-sheet__panel">
                <div className="applicant-sheet__top">
                    <h3 className="applicant-sheet__name">{applicant.name}</h3>
                    <button type="button" className="applicant-sheet__close" onClick={onClose}>Close</button>
                </div>
                <div className="applicant-sheet__player">
                    <Player jobApplication={jobApplication} onPlay={onPlay} />
                </div>
                <p className="applicant-detail__line">{applicant.email}</p>
                <p className="applicant-detail__line">{jobApplication.attributes.formatted_phone_number}</p>
                {applicant.education && (
                    <p className="applicant-detail__line">{applicant.education}</p>
                )}
                {jobApplication.attributes.fun_fact && (
                    <p className="applicant-detail__fact">{jobApplication.attributes.fun_fact}</p>
                )}
                <ApplicantActions
                    jobApplication={jobApplication}
                    allowToggle={allowToggle}
                    onModalOpen={onModalOpen}
                    onHide={handleHide}
                    variant="row"
                />
            </div>
        </div>
    );
};

const JobApplicationList = (props) => {
    const [activePlayer, setActivePlayer] = useState(null);
    const [jobApplications, setJobApplications] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const layout = LAYOUTS.includes(props.layout) ? props.layout : 'table';

    const resetActivePlayer = (player) => {
        if (activePlayer) {
            if (activePlayer !== player) {
                activePlayer.pause();
            }
            setActivePlayer(player)
        } else {
            setActivePlayer(player)
        }

        return activePlayer;
    };

    const handleHideJobApplication = (jobApplicationId) => {
      const newJobApplications = jobApplications.filter((jobApplication) => {
          return jobApplication.id !== jobApplicationId;
      });

      setJobApplications(newJobApplications);
    };

    useEffect(() => {
        setJobApplications(props.jobApplications);
    }, [props.jobApplications])

    useEffect(() => {
        setSelectedId(null);
        if (activePlayer) {
            activePlayer.pause();
        }
    }, [layout])

    useEffect(() => {
        if (selectedId && !jobApplications.find((application) => String(application.id) === String(selectedId))) {
            setSelectedId(null);
        }
    }, [jobApplications, selectedId])

    const selected = jobApplications.find((application) => String(application.id) === String(selectedId));
    const closeSheet = useCallback(() => setSelectedId(null), []);

    const playFor = (jobApplication) => (player) => {
        if (!jobApplication.attributes.viewed) {
            Client.markViewedJobApplication(jobApplication.id);
        }
        resetActivePlayer(player);
    };

    const emptyState = (rows) => {
        if (props.loading) return false;
        return rows.length === 0 && props.empty;
    };

    const sheet = selected ? (
        <ApplicantSheet
            key={selected.id}
            jobApplication={selected}
            allowToggle={props.allowToggle}
            onModalOpen={props.onModalOpen}
            onHide={handleHideJobApplication}
            onPlay={playFor(selected)}
            onClose={closeSheet}
        />
    ) : null;

    if (layout === 'compact') {
        if (emptyState(jobApplications)) return props.empty;
        return (
            <div className="applicant-compact-list">
                {jobApplications.map((jobApplication) => {
                    const attrs = jobApplication.attributes;
                    return (
                        <div className="applicant-compact" key={jobApplication.id}>
                            <button
                                type="button"
                                className="applicant-compact__open"
                                onClick={() => setSelectedId(jobApplication.id)}
                            >
                                <span className="applicant-compact__video">
                                    <ApplicantThumb jobApplication={jobApplication} clickable={false} />
                                </span>
                                <span className="applicant-compact__meta">
                                    <span className="applicant-compact__name">{attrs.applicant.name}</span>
                                    <span className="applicant-compact__sub">{attrs.applicant.email}</span>
                                    {attrs.formatted_phone_number && (
                                        <span className="applicant-compact__sub">{attrs.formatted_phone_number}</span>
                                    )}
                                    {attrs.applicant.education && (
                                        <span className="applicant-compact__sub applicant-compact__extra">{attrs.applicant.education}</span>
                                    )}
                                </span>
                                {attrs.fun_fact && (
                                    <span className="applicant-compact__fact">{attrs.fun_fact}</span>
                                )}
                            </button>
                            <div className="applicant-compact__actions" onClick={(event) => event.stopPropagation()}>
                                <ApplicantActions
                                    jobApplication={jobApplication}
                                    allowToggle={props.allowToggle}
                                    onModalOpen={props.onModalOpen}
                                    onHide={handleHideJobApplication}
                                    variant="row"
                                />
                            </div>
                        </div>
                    );
                })}
                {sheet}
            </div>
        );
    }

    if (layout === 'grid') {
        if (emptyState(jobApplications)) return props.empty;
        return (
            <div>
                <div className="applicant-grid">
                    {jobApplications.map((jobApplication) => (
                        <button
                            type="button"
                            key={jobApplication.id}
                            className="applicant-grid__card"
                            onClick={() => setSelectedId(jobApplication.id)}
                        >
                            <ApplicantThumb jobApplication={jobApplication} clickable={false} />
                            <span className="applicant-grid__name">{jobApplication.attributes.applicant.name}</span>
                        </button>
                    ))}
                </div>
                {sheet}
            </div>
        );
    }

    const rows = jobApplications.map((jobApplication) => {
        return <JobApplication
                  key={jobApplication.id}
                  jobApplication={jobApplication}
                  allowToggle={props.allowToggle}
                  onModalOpen={props.onModalOpen}
                  onPlay={resetActivePlayer}
                  onHide={handleHideJobApplication} />
    })

    let body = rows;
    if (props.loading) {
        body = <SkeletonRows />;
    } else if (rows.length === 0 && props.empty) {
        body = (
            <tr className="applicants-empty-row">
                <td colSpan="4">{props.empty}</td>
            </tr>
        );
    }

    return (
        <table className="table mt-2 applicants-table">
            <colgroup>
                <col className="applicants-col applicants-col--video" />
                <col className="applicants-col applicants-col--info" />
                <col className="applicants-col applicants-col--fact" />
                <col className="applicants-col applicants-col--actions" />
            </colgroup>
            <thead>
            <tr>
                <th scope="col">Video</th>
                <th scope="col">Applicant Info</th>
                <th scope="col">Fun Fact</th>
                <th scope="col">Actions</th>
            </tr>
            </thead>
            <tbody>
                {body}
            </tbody>
        </table>
    )
};

JobApplicationList.propTypes = propTypes;

export default JobApplicationList;
